import React from 'react';
import { ButtonBase, Grid, Typography, useMediaQuery, useTheme } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import { useDroppable } from '@dnd-kit/core';
import { useTranslation } from 'react-i18next';
import { AreaOccupancyData } from '../../../api';
import { AreaSeverity, slotAt } from '../utils/occupancyMath';
import { createAreaDropId } from '../utils/areaDrop';
import { slotBackground } from '../areasPalette';

type AreaSquareProps = {
  schedulerConfigId: string;
  area: AreaOccupancyData;
  label: string;
  slotData: ReturnType<typeof slotAt>;
  severity: AreaSeverity;
  arrivalsInSlot: number;
  isFiltered: boolean;
  isDropEnabled: boolean;
  onToggleFilter: () => void;
};

export const AreaSquare = ({
  schedulerConfigId,
  area,
  label,
  slotData,
  severity,
  arrivalsInSlot,
  isFiltered,
  isDropEnabled,
  onToggleFilter,
}: AreaSquareProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const { setNodeRef, isOver } = useDroppable({
    id: createAreaDropId(schedulerConfigId, area.area_id),
    disabled: !isDropEnabled,
  });
  const isFullyBlocked = severity === AreaSeverity.BLOCKED_ALL;
  const freeLabel = slotData ? `${slotData.free}/${slotData.total}` : '—';

  return (
    <ButtonBase
      ref={setNodeRef}
      onClick={onToggleFilter}
      aria-pressed={isFiltered}
      aria-label={`${label}, ${freeLabel}, ${t('calendar.areas_view.starting_count', {
        count: arrivalsInSlot,
      })}`}
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: isSmallScreen ? 'row' : 'column',
        alignItems: 'center',
        justifyContent: 'center',
        // Phone: a compact one-line chip in a scrolling row, as wide as its content.
        ...(isSmallScreen && { gap: 0.75, flex: '0 0 auto', whiteSpace: 'nowrap' }),
        height: isSmallScreen ? '40px' : '92px',
        borderRadius: '8px',
        overflow: 'hidden',
        paddingInline: isSmallScreen ? 1.25 : 0.5,
        background: slotBackground(slotData),
        border: '1px solid',
        borderColor: 'divider',
        color: severity === AreaSeverity.CRIT ? 'common.white' : 'text.primary',
        // The tapped square is the list's area filter; a ring inside the square says so.
        ...(isFiltered && !isOver && { outline: '3px solid', outlineColor: 'primary.main', outlineOffset: '-3px' }),
        ...(isOver && { outline: '3px dashed', outlineColor: 'primary.main', outlineOffset: '-3px' }),
      }}>
      <Typography
        sx={{
          fontSize: '14px',
          fontWeight: 500,
          ...(!isSmallScreen && { maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }),
          whiteSpace: 'nowrap',
        }}>
        {label}
      </Typography>
      {isOver ? (
        <Typography sx={{ fontSize: '13px', fontWeight: 700 }}>{t('calendar.areas_view.drop_to_move')}</Typography>
      ) : isFullyBlocked ? (
        <Grid container alignItems="center" justifyContent="center" gap={0.5} wrap="nowrap" sx={{ width: 'auto' }}>
          <LockIcon sx={{ fontSize: isSmallScreen ? '16px' : '18px' }} />
          <Typography sx={{ fontSize: '14px', fontWeight: 700 }}>{t('calendar.areas_view.closed')}</Typography>
        </Grid>
      ) : (
        <Typography
          dir="ltr"
          sx={{ fontSize: isSmallScreen ? '14px' : '22px', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
          {freeLabel}
        </Typography>
      )}
      {arrivalsInSlot > 0 && (
        <Grid
          sx={{
            ...(isSmallScreen
              ? { flexShrink: 0, width: '16px', height: '16px', fontSize: '11px' }
              : {
                  position: 'absolute',
                  top: '4px',
                  insetInlineEnd: '4px',
                  width: '18px',
                  height: '18px',
                  fontSize: '12px',
                }),
            borderRadius: '4px',
            backgroundColor: 'primary.main',
            color: 'common.white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
          {arrivalsInSlot}
        </Grid>
      )}
    </ButtonBase>
  );
};
